import { cn } from "@/lib/utils";
import * as React from "react";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  styleLabel?: object;
  labelStyles?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      styleLabel,
      onAnimationStart,
      placeholder,
      labelStyles,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current!);

    const [initialLoad, setInitialLoad] = React.useState(true);

    React.useEffect(() => {
      setTimeout(() => setInitialLoad(false), 500);
    }, []);

    return (
      <div className="relative h-full">
        <textarea
          className={cn(
            "flex peer input h-10 w-full placeholder:opacity-0 focus:placeholder:opacity-0",
            "transition-all duration-300 rounded-md border-2 border-gray-300 focus:border-blue-medium",
            "p-4 pr-3 text-xl ring-offset-white file:border-0 file:bg-transparent",
            "file:text-xl file:font-medium file:text-neutral-950 focus-visible:outline-none",
            "focus-visible:ring-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800",
            "dark:file:text-neutral-50 dark:focus-visible:ring-neutral-300",
            className
          )}
          placeholder={placeholder}
          onAnimationStart={(e) => {
            if (initialLoad && e.animationName === "onAutofill") {
              innerRef.current?.focus();
              innerRef.current?.blur();
              setInitialLoad(false);
            }

            if (onAnimationStart) {
              onAnimationStart(e);
            }
          }}
          ref={innerRef}
          {...props}
        />
        <label
          style={styleLabel}
          className={cn(
            "absolute left-3.5 top-7 transform -translate-y-1/2 px-1 bg-white",
            "text-neutral-500 font-normal text-xl transition-all duration-100 pointer-events-none",
            "peer-focus:top-0 peer-focus:text-sm peer-focus:z-10",
            "peer-focus:text-blue-medium",
            "peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:z-10",
            labelStyles
          )}
        >
          {placeholder}
        </label>
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
